import { theme, type ThemeConfig } from 'antd';
import { tokens } from './tokens';

// X5 — one focus ring for the whole app: a 2px Bright Blue tint. Kept as a colour plus a
// composed shadow because antd asks for the two shapes under different names — `Input`
// takes the whole `activeShadow`, `Select` takes only `activeOutlineColor`.
const focusRingColor = 'rgba(66, 113, 239, 0.15)';
const focusRing = `0 0 0 2px ${focusRingColor}`;

export const appTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,

  token: {
    colorPrimary: tokens.buttonFill,
    colorPrimaryHover: tokens.buttonHover,
    colorInfo: tokens.info,
    colorLink: tokens.linkBlue,
    // X6 — overlay mask is charcoal at 50%, not antd's default black at 45%. Set as the
    // global alias so Modal and Drawer dim identically; `Modal.maskBg` is not a v6 token.
    colorBgMask: tokens.maskCharcoal,
    // A link hover must not *change colour*: Bright Blue is 4.33:1 on white and 3.06:1 on
    // charcoal, so the old `colorLinkHover: brightBlue` made hovered text harder to read
    // than resting text — on three separate surfaces (login card, wizard Skip, breadcrumbs).
    // Breadcrumb rule N6 already ruled this: hover adds an underline, not a hue.
    colorLinkHover: tokens.linkBlue,
    colorLinkActive: tokens.linkBlue,
    linkHoverDecoration: 'underline',
    // X5 — "focus ring = blue tint". antd builds its focus outline as
    // `lineWidthFocus solid colorPrimaryBorder`, and with a charcoal `colorPrimary` that
    // derived to a neutral #474747, so the ring was grey while the inputs beside it used
    // blue. Pointing the alias at the brand blue makes one ring for the whole app.
    colorPrimaryBorder: tokens.brightBlue,
    colorSuccess: tokens.success,
    colorError: tokens.error,
    colorWarning: tokens.warning,

    colorBgBase: tokens.pageBg,
    // NPJB-852: the page behind the cards is the design's parchment grey.
    colorBgLayout: tokens.pageGround,

    colorText: tokens.body,
    colorTextSecondary: tokens.muted,
    colorTextTertiary: tokens.muted,
    colorTextQuaternary: tokens.disabledText,
    colorTextDisabled: tokens.disabledText,
    colorTextPlaceholder: tokens.muted,

    colorBorder: tokens.borderDim,
    colorBorderSecondary: tokens.border,
    colorBgContainerDisabled: tokens.disabledFieldFill,
    // D1 names all three parts of a disabled field: `#F5F5F5` fill, `#C9C9C9` border,
    // `#767676` text. The fill and the text had tokens; the border did not, so antd
    // derived it from `colorBgBase` and shipped an off-palette `#D4D4D4`.
    colorBorderDisabled: tokens.disabledBorder,

    fontFamily: tokens.fontBody,
    fontSize: tokens.fontSizeSm,
    fontSizeSM: tokens.fontSizeXs,
    fontSizeLG: tokens.fontSizeMd,
    fontSizeXL: tokens.fontSizeLg,

    borderRadius: tokens.borderRadius,
    borderRadiusLG: tokens.borderRadiusLg,
    borderRadiusSM: tokens.borderRadius,
    borderRadiusXS: tokens.borderRadius,
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 24,
    wireframe: false,
  },

  components: {
    Layout: {
      headerBg: tokens.charcoal,
      headerColor: tokens.onCharcoal,
      headerHeight: 60,
      siderBg: tokens.pageBg,
      bodyBg: tokens.pageGround,
      lightSiderBg: tokens.pageBg,
    },

    Menu: {
      itemBg: 'transparent',
      itemColor: tokens.body,
      itemHoverBg: tokens.cardBg,
      itemHoverColor: tokens.body,
      itemSelectedBg: tokens.blueTint10,
      itemSelectedColor: tokens.linkBlue,
      itemActiveBg: tokens.blueTint10,
      itemHeight: 40,
      itemMarginBlock: 2,
      itemMarginInline: 4,
      iconSize: 16,
      collapsedIconSize: 18,
      collapsedWidth: 56,
    },

    Button: {
      // Buttons are the ONLY pill shapes in the system (3rd Eye style card).
      borderRadius: tokens.borderRadiusPill,
      borderRadiusLG: tokens.borderRadiusPill,
      borderRadiusSM: tokens.borderRadiusPill,
      defaultBg: tokens.white,
      defaultHoverBg: tokens.white,
      defaultActiveBg: tokens.white,
      defaultColor: tokens.body,
      defaultHoverColor: tokens.charcoal,
      defaultActiveColor: tokens.charcoal,
      defaultBorderColor: tokens.borderDim,
      defaultHoverBorderColor: tokens.buttonHover,
      defaultActiveBorderColor: tokens.buttonHover,
      defaultShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
      // The *dangerous* outlined variant reads the global `colorBgContainer`, which derives
      // from `colorBgBase` (page grey) rather than from `defaultBg` — so it alone sat on
      // #FAFAFA while every other outline button sat on white, reading grey on a white card.
      colorBgContainer: tokens.white,
      // D5 — "opacity is not the mechanism". antd fades a loading button to 0.65, which
      // renders differently on white, pale-blue and charcoal (the exact drift D5 exists to
      // prevent) and makes loading indistinguishable from disabled. The spinner plus the
      // suppressed pointer events carry the state instead (D6: never one signal alone).
      opacityLoading: 1,
      primaryColor: tokens.onPrimary,
      fontWeight: tokens.fontWeightRegular,
      fontSizeLG: tokens.fontSizeMd,
      // B6/B7 are two rules, not one. A *filled* button disables to a `#DCDCDC` fill with a
      // `#5A5A5A` label; an *outline* button keeps its outline — no fill, `#C9C9C9` border,
      // `#767676` label. Mock `18:2` shows exactly that: the "Downloaded" button is an
      // unfilled pill with a light grey edge. These were previously all pointed at the
      // filled treatment, so a disabled secondary button *gained* a fill and stopped
      // reading as secondary.
      colorBgContainerDisabled: tokens.disabledFill,
      borderColorDisabled: tokens.disabledBorder,
      defaultBgDisabled: tokens.white,
      dashedBgDisabled: tokens.white,
      // Filled label. antd shares one `colorTextDisabled` across variants, so the outline
      // label is corrected in `global.css` — there is no per-variant token for it.
      colorTextDisabled: tokens.disabledFillLabel,
    },

    Card: {
      colorBgContainer: tokens.white,
      colorBorderSecondary: tokens.border,
      boxShadowTertiary: 'none',
      paddingLG: tokens.space4_75,
    },

    Table: {
      headerBg: tokens.charcoal,
      headerColor: tokens.onCharcoal,
      // No rule between header cells — the charcoal band reads as one solid header, and
      // the column labels do the separating (style card).
      headerSplitColor: 'transparent',
      // Sortable headers: antd's defaults are light greys built for a light header, which
      // on our charcoal header washed the cell out to #EFF0F0 and left the white label
      // invisible on hover. Lift to the charcoal hover tone instead.
      headerSortHoverBg: tokens.charcoalDim,
      headerSortActiveBg: tokens.charcoalDim,
      // Sorted-column body tint. antd's default is `colorFillAlterSolid` — a
      // translucent black wash that composites to a different grey on every surface
      // (#F9FAFA over white, something else over a card). D5: the fill is the
      // mechanism, and it has to be a flat token. Matches `rowHoverBg` visually.
      bodySortBg: tokens.pageBg,
      rowHoverBg: tokens.pageBg,
      borderColor: tokens.border,
      cellPaddingBlock: tokens.space3,
      cellPaddingInline: tokens.space4,
      colorBgContainer: tokens.white,
    },

    Input: {
      colorBgContainer: tokens.white,
      colorBorder: tokens.borderDim,
      activeBorderColor: tokens.brightBlue,
      hoverBorderColor: tokens.brightBlue,
      activeShadow: focusRing,
      borderRadius: tokens.borderRadiusSm,
      borderRadiusLG: tokens.borderRadiusSm,
      borderRadiusSM: tokens.borderRadiusSm,
    },

    Select: {
      colorBgContainer: tokens.white,
      colorBgElevated: tokens.white,
      activeBorderColor: tokens.brightBlue,
      hoverBorderColor: tokens.brightBlue,
      // X5 — the focus ring is a blue tint. antd builds a Select's ring from the
      // `controlOutline` alias, which derives from `colorPrimary` (charcoal `#212121`) and
      // landed on `rgba(3, 3, 3, 0.62)` — a near-black wash, beside a text input in the same
      // toolbar showing the blue one. `Input` already overrides its own; this is the Select
      // half of the same override.
      activeOutlineColor: focusRingColor,
      optionSelectedBg: tokens.blueTint10,
      optionActiveBg: tokens.cardBg,
      multipleItemBg: 'transparent',
      multipleItemBorderColor: 'transparent',
      borderRadius: tokens.borderRadiusSm,
      borderRadiusLG: tokens.borderRadiusSm,
      borderRadiusSM: tokens.borderRadiusSm,
    },

    InputNumber: {
      colorBgContainer: tokens.white,
      borderRadius: tokens.borderRadiusSm,
      borderRadiusLG: tokens.borderRadiusSm,
      borderRadiusSM: tokens.borderRadiusSm,
    },

    DatePicker: {
      colorBgContainer: tokens.white,
      borderRadius: tokens.borderRadiusSm,
      borderRadiusLG: tokens.borderRadiusSm,
      borderRadiusSM: tokens.borderRadiusSm,
    },

    Modal: {
      contentBg: tokens.white,
      headerBg: tokens.white,
      // Set globally so the upload/settings dialogs stop re-declaring it locally — seven
      // of them each carried a private ConfigProvider that repainted all three surfaces
      // pure `#ffffff`, overriding this token's `#FEFFFF` with a *different* white.
      footerBg: tokens.white,
      // One dialog header everywhere (NPJB-818 AC3, style-coverage E3): body font, 16/600.
      // The weight is antd's `fontWeightStrong` (600); only the size is pinned here.
      titleFontSize: tokens.fontSizeMd,
    },

    Drawer: {
      colorBgElevated: tokens.white,
    },

    Tooltip: {
      colorBgSpotlight: tokens.charcoal,
    },

    Popover: {
      colorBgElevated: tokens.white,
    },

    Notification: {
      // C9 — a floating surface takes the panel white. Notification was the ONE floating
      // component with no theme block, so it derived `colorBgElevated` from the global
      // `colorBgBase` (page grey) and painted toasts `#FAFAFA` — the page colour, floating
      // above the page. Select, Modal, Popover and Dropdown all set this explicitly; the
      // toast was missed because it auto-dismisses and nothing had measured it
      // (style-coverage 32 · D2).
      colorBgElevated: tokens.white,
    },

    Dropdown: {
      colorBgElevated: tokens.white,
      // R8 — every dropdown's item divider. antd's default is an alpha wash
      // (`rgba(6,6,6,0.09)`), which is a *composite* rather than a token: it shifts with
      // whatever surface it lands on. Two dropdowns were caught shipping it independently
      // (the card kebab and the header user menu), so it is set here once rather than
      // fought per-component with a deeper selector.
      colorSplit: tokens.border,
      // Item hover was antd's `rgba(0,0,0,0.04)` — an alpha wash, so its apparent colour
      // depends on the surface under it. The kebab *trigger* right beside these items
      // already hovers to `--dz-card-bg`; now the menu it opens matches.
      controlItemBgHover: tokens.cardBg,
    },

    Pagination: {
      // Same alpha-wash problem as the dropdown items above (`rgba(0,0,0,0.06)`).
      colorBgTextHover: tokens.cardBg,
      colorBgTextActive: tokens.cardBg,
    },

    Badge: {
      dotSize: 6,
    },

    Switch: {
      colorPrimary: tokens.brightBlue,
      colorPrimaryHover: tokens.linkBlue,
    },

    // NPJB-852 — the design's tab row: 12px labels (design 13, rounded down), muted until
    // selected, charcoal when selected with the 2px ink bar as the state, packed 2px apart on a
    // #C9C9C9 rule. Every label is 600 (`global.css`), so selecting one never reflows the row.
    Tabs: {
      titleFontSize: tokens.fontSizeXs,
      horizontalItemPadding: '0.625rem 0.75rem',
      horizontalItemGutter: 2,
      horizontalMargin: '0 0 18px 0',
      inkBarColor: tokens.brightBlue,
      itemColor: tokens.muted,
      itemSelectedColor: tokens.charcoal,
      itemHoverColor: tokens.brightBlue,
      colorBorderSecondary: tokens.disabledBorder,
    },

    Divider: {
      marginLG: tokens.space4,
      margin: tokens.space2,
      colorSplit: tokens.border,
    },

    // Breadcrumbs (rule catalog "N — Breadcrumbs"). The trail's whole hierarchy lives
    // here, so `PageBreadcrumb` never re-specifies a colour:
    //   ancestors  Link Blue  — they are small links, and C4 reserves Bright Blue for ≥24px
    //   current    body       — the only full-strength crumb; that contrast says "you are here"
    //   separator  border-dim — subordinate to every crumb, but `border` (#E4E4E4) is
    //                           ~1.2:1 on white and reads as missing, so it is NOT used here
    Breadcrumb: {
      // Caption scale for the whole trail, separators included — set here rather than
      // per-crumb so the `/` can't render a size larger than the crumbs (it did at
      // antd's default 14px while the crumb text was 12px).
      fontSize: 12,
      separatorColor: tokens.borderDim,
      itemColor: tokens.muted,
      lastItemColor: tokens.body,
      linkColor: tokens.linkBlue,
      linkHoverColor: tokens.linkBlue,
      colorBgTextHover: 'transparent',
    },

    Progress: {
      defaultColor: tokens.brightBlue,
    },

    Spin: {
      colorPrimary: tokens.brightBlue,
    },

    Skeleton: {
      colorFill: tokens.cardBg,
      colorFillContent: tokens.pageBg,
    },

    Form: {
      labelColor: tokens.body,
      labelFontSize: tokens.fontSizeSm,
      labelHeight: 'auto',
      itemMarginBottom: tokens.space4,
      verticalLabelPadding: '0 0 8px',
    },

    // A status Tag has to read as the same object as a `StatusPill` with the
    // matching tone. antd derives `color*Bg`/`color*Border` from the seed colours
    // when they aren't given, and on our desaturated seeds that derivation lands
    // off-palette (`#B1BDB7` behind `success`, 2.4:1) — C8 sanctions no such
    // surface. Same explicit mapping the Alert block below uses.
    Tag: {
      // Plain `<Tag>` sat on antd's derived `#F0F0F0`, which is not one of C9's
      // three surfaces. Card grey matches FileKindTag / SectionCountPill.
      defaultBg: tokens.cardBg,
      defaultColor: tokens.body,
      colorInfoBg: tokens.infoBg,
      colorInfoBorder: tokens.infoBorder,
      colorSuccessBg: tokens.successBg,
      colorSuccessBorder: tokens.successBorder,
      colorWarningBg: tokens.warningBg,
      colorWarningBorder: tokens.warningBorder,
      colorErrorBg: tokens.errorBg,
      colorErrorBorder: tokens.errorBorder,
      // Tag set the tint and the edge but left the *ink* to antd, which derives it from the
      // global `colorInfo`/`colorSuccess`/`colorWarning`. Those are mid-strength hues built
      // for icons and charts on white, and on their own pale tint at 10px they measured
      // 4.12 / 3.98 / 2.43:1 — all under AA. Each tonal family already ships the value meant
      // to sit on its tint: the `-shade` step. Scoped to Tag so charts, buttons and status
      // icons keep the mid-strength hue.
      colorInfo: tokens.tonalBlueShade,
      colorSuccess: tokens.tonalSageShade,
      colorWarning: tokens.tonalPeachShade,
    },

    Alert: {
      colorInfoBg: tokens.infoBg,
      colorInfoBorder: tokens.infoBorder,
      colorSuccessBg: tokens.successBg,
      colorSuccessBorder: tokens.successBorder,
      colorWarningBg: tokens.warningBg,
      colorWarningBorder: tokens.warningBorder,
      colorErrorBg: tokens.errorBg,
      colorErrorBorder: tokens.errorBorder,
      // R2 — an inline alert sits nested inside a card body, so it takes the nested-block
      // step, not R3's 14px card radius. R13 also asks info boxes to be "subtle-rounded",
      // and 14 is the least subtle radius the system has.
      borderRadiusLG: tokens.borderRadiusMd,
    },

    Steps: {
      colorPrimary: tokens.brightBlue,
    },

    Checkbox: {
      colorPrimary: tokens.brightBlue,
      colorPrimaryHover: tokens.linkBlue,
      colorBgContainer: tokens.white,
    },

    Radio: {
      colorPrimary: tokens.brightBlue,
      colorPrimaryHover: tokens.linkBlue,
      colorBgContainer: tokens.white,
    },

    Segmented: {
      // The selected thumb shipped antd's `boxShadowTertiary` — three translucent black
      // layers rather than one `--dz-shadow-*` token, an alpha wash that composites
      // differently on every ground. Same fix `Card` already carries.
      boxShadowTertiary: 'none',
      itemSelectedBg: tokens.charcoal,
      // I3/C1 — the label on the selected charcoal thumb is on-dark text, so it takes the
      // on-charcoal token. It was `pageBg`, a *surface* token doing a text job — the same
      // substitution spec 31 · D11 made on `IconBadge`.
      itemSelectedColor: tokens.onCharcoal,
      trackBg: tokens.white,
      itemColor: tokens.blueMuted,
      itemHoverColor: tokens.charcoal,
    },
  },
};
